/**
 * 认证业务逻辑 Hook
 * 封装所有认证相关的 API 调用和状态管理
 */
import { useState, useCallback, useRef } from 'react';
import { toast } from '@leadsnavi/ui-kit';
import type { AuthStep } from '@/components/auth-content/types';
import { useCountdown } from '@/components/auth-content/hooks/useCountdown';
import { authChannel } from './useAuthChannel';
import { isLoggedIn } from '@/utils/auth';
import useUserStore from '@/stores/userStore';

// Auth API imports
import { sendVerifyEmailGet } from '@/services/swagger/easesourcing-auth/EmailVerifyService';
import {
  emailPwdLoginPost,
  emailSignUpPost,
  emailSignUpCheckPost,
  forgetPasswordPost,
} from '@/services/swagger/easesourcing-auth/UserLoginService';

const INCOMPLETE_REGISTRATION_CODE = 1003;

/** 邮箱检查状态 */
export type EmailSignUpCheckStatus = 'UN_SIGN_UP' | 'ALREADY_SIGN_UP' | 'ALREADY_VERIFIED';

/** useAuth Hook 的配置选项 */
export interface UseAuthOptions {
  /** 初始步骤 */
  initialStep?: AuthStep;
  /** 初始邮箱 */
  initialEmail?: string;
  /** 邮件链接中的加密参数 */
  cs?: string;
  /** 登录成功回调 */
  onLoginSuccess?: () => void;
  /** 注册成功回调 */
  onSignUpSuccess?: () => void;
  /** 关闭弹窗回调 */
  onClose?: () => void;
}

/**
 * 认证业务逻辑 Hook
 */
export function useAuth(options: UseAuthOptions = {}) {
  const {
    initialStep = 'signup',
    initialEmail = '',
    cs: initialCs,
    onLoginSuccess,
    // onSignUpSuccess,
    onClose,
  } = options;

  // 状态
  const [step, setStep] = useState<AuthStep>(initialStep);
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 邮件链接中的加密参数
  const csRef = useRef<string | undefined>(initialCs);

  // 倒计时（用于重发邮件）
  const { seconds: resendCountdown, start: startCountdown, reset: resetCountdown } = useCountdown(300);

  /**
   * 清除错误
   */
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  /**
   * 设置错误
   */
  const setError = useCallback((key: string, message: string) => {
    setErrors(prev => ({ ...prev, [key]: message }));
  }, []);

  /**
   * 处理步骤切换
   */
  const handleStepChange = useCallback(
    (newStep: AuthStep) => {
      clearErrors();
      setStep(newStep);

      // 如果切换到需要倒计时的步骤，启动倒计时
      if (newStep === 'verify-email' || newStep === 'forgot-success') {
        startCountdown();
      } else {
        resetCountdown();
      }
    },
    [clearErrors, startCountdown, resetCountdown]
  );

  /**
   * 注册 - 检查邮箱并创建账号（后端自动发送验证邮件）
   */
  const handleSignUpSubmit = useCallback(
    async (inputEmail: string, password: string, agreedToPolicy: boolean) => {
      if (!agreedToPolicy) {
        setError('policy', 'Please agree to the privacy policy');
        return;
      }

      setLoading(true);
      clearErrors();

      try {
        // 1. 检查邮箱状态
        const checkResult = await emailSignUpCheckPost({ email: inputEmail });

        if (!checkResult.success) {
          setError('email', checkResult.message || 'Failed to check email');
          return;
        }

        const status = checkResult.data?.emailSignUpCheckStatus;

        if (status === 'ALREADY_VERIFIED') {
          // 邮箱已注册且已验证，提示去登录
          setError('email', 'This email is already registered. Please log in.');
          return;
        }

        // 2. 调用注册接口（后端自动发送验证邮件）
        const signUpResult = await emailSignUpPost(
          {
            email: inputEmail,
            pwd: password,
          },
          { skipGlobalErrorHandling: true }
        );

        if (!signUpResult.success) {
          setError('email', signUpResult.message || 'Registration failed');
          return;
        }

        // 3. 切换到验证邮箱步骤
        setEmail(inputEmail);
        handleStepChange('verify-email');
        toast.success('Verification email sent! Please check your inbox.');
      } catch (error: unknown) {
        console.error('Sign up error:', error);

        const apiError = error as { code?: number; message?: string };

        // code 1003: 账号注册未完成，立即跳转到验证邮箱步骤，后台重发验证邮件
        if (apiError?.code === INCOMPLETE_REGISTRATION_CODE) {
          setEmail(inputEmail);
          handleStepChange('verify-email');
          toast.info('Verification email sent! Please check your inbox.');
          sendVerifyEmailGet({ bizType: 'signup', verifyEmail: inputEmail }, { skipGlobalErrorHandling: true }).catch(
            () => {}
          );
          return;
        }

        if (apiError?.message) {
          setError('email', apiError.message);
        } else {
          setError('email', 'An error occurred. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    },
    [clearErrors, setError, handleStepChange]
  );

  /**
   * 登录
   */
  const handleLoginSubmit = useCallback(
    async (inputEmail: string, password: string, agreedToPolicy: boolean) => {
      if (!agreedToPolicy) {
        setError('policy', 'Please agree to the privacy policy');
        return;
      }

      setLoading(true);
      clearErrors();

      try {
        const result = await emailPwdLoginPost(
          {
            email: inputEmail,
            pwd: password,
          },
          { skipGlobalErrorHandling: true }
        );

        if (!result.success) {
          if (result.message?.toLowerCase().includes('password')) {
            setError('password', result.message || 'Invalid password');
          } else if (
            result.message?.toLowerCase().includes('email') ||
            result.message?.toLowerCase().includes('user')
          ) {
            setError('email', result.message || 'Email not found');
          } else {
            setError('password', result.message || 'Login failed');
          }
          return;
        }

        // 登录成功（后端已设置 Cookie）
        toast.success('Login successful!');

        if (isLoggedIn()) {
          await useUserStore.getState().fetchUser({ isAfterLoginSuccess: true });
        }

        // 广播登录成功事件（通知其他 Tab）
        authChannel.broadcastAuthSuccess();

        // 调用成功回调
        onLoginSuccess?.();
        onClose?.();
      } catch (error: unknown) {
        console.error('Login error:', error);

        const apiError = error as { code?: number; message?: string };

        // code 1003: 账号注册未完成，立即跳转到验证邮箱步骤，后台重发验证邮件
        if (apiError?.code === INCOMPLETE_REGISTRATION_CODE) {
          setEmail(inputEmail);
          handleStepChange('verify-email');
          toast.info('Verification email sent! Please check your inbox.');
          sendVerifyEmailGet({ bizType: 'signup', verifyEmail: inputEmail }, { skipGlobalErrorHandling: true }).catch(
            () => {}
          );
          return;
        }

        // 其他 API 错误
        if (apiError?.message) {
          if (apiError.message.toLowerCase().includes('password')) {
            setError('password', apiError.message);
          } else if (
            apiError.message.toLowerCase().includes('email') ||
            apiError.message.toLowerCase().includes('user')
          ) {
            setError('email', apiError.message);
          } else {
            setError('password', apiError.message);
          }
        } else {
          setError('password', 'An error occurred. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    },
    [clearErrors, setError, handleStepChange, onLoginSuccess, onClose]
  );

  /**
   * 忘记密码 - 发送重置邮件
   */
  const handleForgotPasswordSubmit = useCallback(
    async (inputEmail: string) => {
      setLoading(true);
      clearErrors();

      try {
        const result = await sendVerifyEmailGet({
          bizType: 'forgetPwd',
          verifyEmail: inputEmail,
        });

        if (!result.success) {
          setError('email', result.message || 'Failed to send reset email');
          return;
        }

        // 切换到发送成功步骤
        setEmail(inputEmail);
        handleStepChange('forgot-success');
        toast.success('Reset email sent! Please check your inbox.');
      } catch (error) {
        console.error('Forgot password error:', error);
        setError('email', 'An error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [clearErrors, setError, handleStepChange]
  );

  /**
   * 重置密码
   */
  const handleResetPasswordSubmit = useCallback(
    async (newPassword: string, confirmPassword: string) => {
      if (newPassword !== confirmPassword) {
        setError('confirmPassword', "Passwords don't match");
        return;
      }

      if (!csRef.current) {
        setError('password', 'Invalid reset link. Please request a new one.');
        handleStepChange('reset-failed');
        return;
      }

      setLoading(true);
      clearErrors();

      try {
        const result = await forgetPasswordPost({
          cs: csRef.current,
          newPassword: newPassword,
        });

        if (!result.success) {
          setError('password', result.message || 'Failed to reset password');
          return;
        }

        // 重置成功，切换到登录页面
        toast.success('Password reset successfully! Please log in.');
        handleStepChange('login');
      } catch (error) {
        console.error('Reset password error:', error);
        // 重置失败，切换到失败页面
        handleStepChange('reset-failed');
      } finally {
        setLoading(false);
      }
    },
    [clearErrors, setError, handleStepChange]
  );

  /**
   * 重发验证邮件
   */
  const handleResendClick = useCallback(async () => {
    if (resendCountdown > 0) return;

    setLoading(true);

    try {
      const bizType = step === 'verify-email' ? 'signup' : 'forgetPwd';

      const result = await sendVerifyEmailGet({
        bizType,
        verifyEmail: email,
      });

      if (result.success) {
        toast.success('Email sent successfully!');
        startCountdown();
      } else {
        toast.error(result.message || 'Failed to resend email');
      }
    } catch (error) {
      console.error('Resend email error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, step, resendCountdown, startCountdown]);

  /**
   * 点击忘记密码链接
   */
  const handleForgotPasswordClick = useCallback(() => {
    handleStepChange('forgot-password');
  }, [handleStepChange]);

  /**
   * Google OAuth 跳转地址
   */
  const googleAuthUrl = '/auth/api/pub/oauth/render/google';

  /**
   * 点击隐私政策链接
   */
  const handlePrivacyPolicyClick = useCallback(() => {
    // 打开隐私政策页面
    window.open('https://www.easesourcing.com/privacy-notice', '_blank');
  }, []);

  /**
   * 设置 cs 参数（用于邮件链接回调）
   */
  const setCs = useCallback((cs: string) => {
    csRef.current = cs;
  }, []);

  return {
    // 状态
    step,
    email,
    loading,
    errors,
    resendCountdown,

    // 方法
    setStep: handleStepChange,
    setEmail,
    setCs,
    clearErrors,

    // 事件处理
    onStepChange: handleStepChange,
    onSignUpSubmit: handleSignUpSubmit,
    onLoginSubmit: handleLoginSubmit,
    onForgotPasswordSubmit: handleForgotPasswordSubmit,
    onResetPasswordSubmit: handleResetPasswordSubmit,
    onResendClick: handleResendClick,
    onForgotPasswordClick: handleForgotPasswordClick,
    googleAuthUrl,
    onPrivacyPolicyClick: handlePrivacyPolicyClick,
  };
}

export default useAuth;
